import WalletService from "@app/services/http/wallet.service";
import { DailyActivity, FeatureInformation } from "@app/types";
import { useEffect, useMemo, useState } from "react";
import StarSmIcon from "@app/icons/starSm";
import StarLgIcon from "@app/icons/starLg";
import SavingIcon from "@app/icons/saving";
import AddIcon from "@app/icons/add";
import InfoIcon from "@app/icons/info";
import clsx from "clsx";
import styles from "./styles.module.scss";
import Button from "@app/components/button";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import {
  DailyActivityItemStatus,
  FeatureName,
  RoutePath,
} from "@app/constants";
import FeatureInformationService from "@app/services/http/feature-information.service";
import { formatMoneyVN } from "@app/utils/util";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";

interface CheckInPointProps {
  status: DailyActivityItemStatus;
  value: number;
}

function CheckInPoint({ status, value }: CheckInPointProps) {
  const isDisable = status !== DailyActivityItemStatus.Received;
  const isCanReceive = status === DailyActivityItemStatus.CanReceive;

  return (
    <div
      className={clsx(
        styles["check-in-point"],
        "relative w-[40px] h-[44px] flex items-end justify-center rounded-[4px]",
        {
          "border-[1px] border-[#FF7029]": isCanReceive,
          "bg-[linear-gradient(180deg,#CCCCCC_0%,#E5E5E5_55.25%)]": isDisable,
        }
      )}
    >
      <p
        className={clsx("text-[16px] font-semibold", {
          "text-[#FF7029]": isCanReceive,
          "text-white": !isCanReceive,
        })}
      >
        {value}
      </p>

      <div
        className={clsx(
          "absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 transform flex items-center justify-center w-[29px] h-[29px] rounded-full",
          {
            "bg-[#EBE88A]": isDisable,
            "bg-[#F7F363]": !isDisable,
          }
        )}
      >
        <div
          className={clsx(
            "flex items-center justify-center w-[21px] h-[21px] rounded-full bg-[#F9B938]",
            {
              "bg-[#FCD078]": isDisable,
              "bg-[#F9B938]": !isDisable,
            }
          )}
        >
          <StarLgIcon />
        </div>
      </div>
    </div>
  );
}

function WalletUser() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<DailyActivity>();
  const [update, forceUpdate] = useForceUpdate();
  const [featureInformation, setFeatureInformation] = useState<
    FeatureInformation[]
  >([]);

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    FeatureInformationService.get(abortController.signal).then((res) => {
      setFeatureInformation(res.data.items);
    });
  }, []);

  useEffect(() => {
    WalletService.get(abortController.signal).then((res) => {
      setWallet(res.data);
    });
  }, [update]);

  const isCanCheckIn = () => {
    if (!wallet) return false;

    return wallet.items.some(
      (item) => item.status === DailyActivityItemStatus.CanReceive
    );
  };

  const daylyLoginFeatureInformation = useMemo(() => {
    return featureInformation.find(
      (item) => item.featureName === FeatureName.DAILY_LOGIN
    );
  }, [featureInformation]);

  const handleCheckIn = () => {
    WalletService.checkIn().then(() => {
      forceUpdate();
    });
  };

  return (
    <div className="mt-[60px] font-[SVN-Gilroy]">
      <div className="flex xl:flex-row flex-col gap-4 justify-between">
        {/* Left */}
        <div className="w-[343px] flex flex-col gap-[40px]">
          <div className="w-[252px] flex items-stretch">
            <div className="flex-1 flex flex-col justify-between">
              <div className="flex gap-1 items-center">
                <div className="flex items-center justify-center w-[20px] h-[20px] rounded-full bg-[#F7F363]">
                  <div className="flex items-center justify-center w-[13px] h-[13px] rounded-full bg-[#F9B938]">
                    <StarSmIcon />
                  </div>
                </div>
                <p className="text-[14px] font-medium">Kas của tôi</p>
              </div>
              <p className="text-[40px] font-semibold text-[#FFD500] mt-auto">
                {wallet?.totalPoint && formatMoneyVN(wallet.totalPoint)}
              </p>
            </div>
            <div>
              <SavingIcon />
            </div>
          </div>

          <div className="bg-[#ECEEF2] h-[104px] rounded-3xl flex flex-col items-center justify-center gap-2">
            <div
              className="w-[44px] h-[44px] bg-[#5A82D7] rounded-full flex items-center justify-center cursor-pointer"
              onClick={() => navigate(RoutePath.VAS_OPTION_LIST)}
            >
              <AddIcon />
            </div>
            <p className="text-[14px] font-semibold">Nạp Kas</p>
          </div>
        </div>

        {/* Right */}
        <div className="w-[343px]">
          <div
            className={clsx(
              styles["check-in"],
              "h-[140px] rounded-2xl px-[8px] pt-[16px] pb-[8px] flex flex-col justify-between"
            )}
          >
            <div className="flex justify-between">
              <p className="text-[14px] font-semibold text-white pl-4">
                Đăng nhập {wallet?.totalDaily} ngày liên tiếp
              </p>
              <div data-tooltip-id={"infoIcon"} className="cursor-pointer">
                <InfoIcon />
              </div>
              <Tooltip
                id={"infoIcon"}
                place="top"
                variant="light"
                content={daylyLoginFeatureInformation?.information}
                className="max-w-[200px] rounded-[4px] z-50"
              />
            </div>
            <div className="h-[82px] bg-white rounded-lg flex items-center justify-center">
              <div className="flex gap-1">
                {wallet?.items.map((item, index) => (
                  <CheckInPoint
                    key={index}
                    value={item.point}
                    status={item.status}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-4">
            <Button
              width="auto"
              className={clsx(
                styles["btn-check-in"],
                "mt-5 !w-[152px] text-sm !h-11 font-bold !rounded-[22px]",
                {
                  "opacity-70 !cursor-default": !isCanCheckIn(),
                }
              )}
              label="Điểm danh"
              labelClassName="font-[SVN-Gilroy] tracking-[0.028px]"
              disabled={!isCanCheckIn()}
              onClick={handleCheckIn}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default WalletUser;
